import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Stack,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
  LinearProgress,
  Backdrop,
  CircularProgress,
  alpha,
  Badge,
  Fade,
  Autocomplete,
  Skeleton,
} from "@mui/material";
import {
  Add,
  Close,
  Delete,
  Edit,
  Image,
  Visibility,
  CheckCircle,
  Cancel,
  PhotoCamera,
  Inventory,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  History as HistoryIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Block as BlockIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import { MenuButton } from "../../../components/Menu";
import { baseUrl, config, requestID } from "../../../common/utils";

interface Room {
  id: number;
  product_id: string;
  name: string;
  price: string;
  stock_value: string;
  category: string;
  type: string;
  qty: number;
  instocks: number;
  totalQtyPosted: number;
  totalQtySold: number;
  room_number?: string;
  room_name?: string;
  status?: string;
}

interface LostAndFoundItem {
  id?: number;
  item_name: string;
  item_image?: string;
  notes?: string;
  return_status?: 'PENDING' | 'RETURNED';
  returned_by?: string;
  returned_by_name?: string;
  returned_at?: string;
  returned_to_name?: string;
  returned_to_phone?: string;
  return_notes?: string;
}

interface LostAndFoundHeader {
  id: number;
  branch_id: number;
  business_id: number;
  room_id: number;
  room_name?: string;
  room_number?: string;
  captured_by: string;
  captured_by_name?: string;
  captured_at: string;
  notes?: string;
  items: LostAndFoundItem[];
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: any;
}

// Status options
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Items', color: '#3b82f6', icon: <Inventory /> },
  { value: 'not_returned', label: 'Pending', color: '#f59e0b', icon: <Cancel /> },
  { value: 'returned', label: 'Returned', color: '#10b981', icon: <CheckCircle /> },
];

const RegisterLostAndFound: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for rooms
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  // State for lost and found items
  const [lostAndFoundList, setLostAndFoundList] = useState<LostAndFoundHeader[]>([]);
  const [lostAndFoundLoading, setLostAndFoundLoading] = useState(false);
  const [lostAndFoundError, setLostAndFoundError] = useState<string | null>(null);

  // State for dialogs
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState<LostAndFoundHeader | null>(null);
  const [selectedItem, setSelectedItem] = useState<LostAndFoundItem | null>(null);

  // State for image preview
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewItemName, setPreviewItemName] = useState('');

  // State for form data
  const [formData, setFormData] = useState({
    room_id: '',
    notes: '',
    items: [] as LostAndFoundItem[]
  });

  // State for current item being added
  const [currentItem, setCurrentItem] = useState<LostAndFoundItem>({
    item_name: '',
    item_image: '',
    notes: ''
  });

  // State for return form
  const [returnForm, setReturnForm] = useState({
    returned_to_name: '',
    returned_to_phone: '',
    return_notes: ''
  });

  // State for loading and feedback
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // State for list and filtering
  const [filteredList, setFilteredList] = useState<LostAndFoundHeader[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // State for statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    returned: 0
  });

  // Fetch rooms on component mount
  useEffect(() => {
    fetchRooms();
       fetchLostAndFoundItems();
  }, []);

  // Fetch lost and found items after rooms are loaded or separately
  useEffect(() => {
     fetchRooms();
    fetchLostAndFoundItems();
  }, []);

  // Apply filters when list, search, or status changes
  useEffect(() => {
    applyFilters();
  }, [lostAndFoundList, searchTerm, selectedStatus]);

  // Calculate stats when list changes
  useEffect(() => {
    calculateStats();
  }, [lostAndFoundList]);

  // Fetch rooms only
  const fetchRooms = async () => {
    setRoomsLoading(true);
    setRoomsError(null);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Housekeepers/getRoomsByBranch`, formData, config);
      
      console.log("Rooms API Response:", response.data);
      
      if (response.data.status === "true" || response.data.status === true) {
        const mappedRooms = response.data.data.map((room: any) => ({
          ...room,
          room_number: extractRoomNumber(room.name),
          room_name: room.name,
          status: room.item_status || 'available'
        }));
        setRooms(mappedRooms);
        console.log("Rooms loaded:", mappedRooms.length);
      } else {
        setRoomsError('Failed to load rooms');
        showSnackbar('Failed to load rooms', 'error');
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setRoomsError('Error loading rooms');
      showSnackbar('Error loading rooms', 'error');
    } finally {
      setRoomsLoading(false);
    }
  };

  // Fetch lost and found items only
  const fetchLostAndFoundItems = async () => {
    setLostAndFoundLoading(true);
    setLostAndFoundError(null);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Lostandfounds/viewLostAndFound`, formData, config);
      
      console.log("Lost & Found API Response:", response.data);
      
      if (response.data.status) {
        // Enrich with room names using the already loaded rooms
        const enrichedData = response.data.data.map((header: LostAndFoundHeader) => {
          const room = rooms.find(r => r.id === header.room_id || r.product_id === header.room_id.toString());
          return {
            ...header,
            room_name: header.room_name || `Room ${header.room_id}`,
            room_number: room ? extractRoomNumber(room.name) : header.room_id.toString()
          };
        });
        setLostAndFoundList(enrichedData || []);
        console.log("Lost & Found items loaded:", enrichedData.length);
      } else {
        setLostAndFoundError('Failed to load lost and found items');
        showSnackbar(response.data.message || 'Failed to fetch records', 'error');
      }
    } catch (error) {
      console.error('Error fetching lost and found items:', error);
      setLostAndFoundError('Error loading lost and found items');
      showSnackbar('Error fetching records', 'error');
    } finally {
      setLostAndFoundLoading(false);
    }
  };

  // Refresh both data sources
  const handleRefresh = async () => {
    await fetchRooms();
    await fetchLostAndFoundItems();
    showSnackbar('Data refreshed', 'info');
  };

  const extractRoomNumber = (roomName: string): string => {
    const match = roomName.match(/ROOM\s+(\d+)/i);
    return match ? match[1] : roomName;
  };

  const calculateStats = () => {
    let pending = 0;
    let returned = 0;
    
    lostAndFoundList.forEach(header => {
      header.items.forEach(item => {
        if (item.return_status === 'RETURNED') {
          returned++;
        } else {
          pending++;
        }
      });
    });

    setStats({
      total: pending + returned,
      pending,
      returned
    });
  };

  const applyFilters = () => {
    let filtered = [...lostAndFoundList];

    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(header => 
        header.items.some(item => 
          item.return_status?.toLowerCase() === selectedStatus.toLowerCase()
        )
      );
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(header => 
        header.room_name?.toLowerCase().includes(term) ||
        header.room_number?.includes(term) ||
        header.captured_by_name?.toLowerCase().includes(term) ||
        header.items.some(item => 
          item.item_name.toLowerCase().includes(term) ||
          item.notes?.toLowerCase().includes(term)
        )
      );
    }

    setFilteredList(filtered);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAddItem = () => {
    if (!currentItem.item_name.trim()) {
      showSnackbar('Item name is required', 'error');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...currentItem, id: Date.now() }] // temporary id
    }));

    // Reset current item
    setCurrentItem({
      item_name: '',
      item_image: '',
      notes: ''
    });
  };

  const handleRemoveItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentItem(prev => ({
          ...prev,
          item_image: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (imageSrc: string, itemName: string) => {
    setPreviewImage(imageSrc);
    setPreviewItemName(itemName);
    setOpenImagePreview(true);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.room_id) {
      showSnackbar('Please select a room', 'error');
      return;
    }

    if (formData.items.length === 0) {
      showSnackbar('Please add at least one item', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append("requestID", requestID());
      submitData.append("room_id", formData.room_id);
      submitData.append("notes", formData.notes || '');
      submitData.append("items", JSON.stringify(formData.items.map(item => ({
        item_name: item.item_name,
        item_image: item.item_image,
        notes: item.notes
      }))));

      const response = await axios.post(`${baseUrl()}Lostandfounds/addLostAndFound`, submitData, config);
      
      if (response.data.status) {
        showSnackbar('Lost and found items added successfully', 'success');
        setOpenAddDialog(false);
        // Reset form
        setFormData({
          room_id: '',
          notes: '',
          items: []
        });
        setSelectedRoom(null);
        // Refresh lost and found list
        await fetchLostAndFoundItems();
      } else {
        showSnackbar(response.data.message || 'Failed to add items', 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showSnackbar('Error submitting form', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnItem = async () => {
    if (!selectedItem) return;

    if (!returnForm.returned_to_name.trim()) {
      showSnackbar('Recipient name is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("item_id", selectedItem.id?.toString() || '');
      formData.append("returned_to_name", returnForm.returned_to_name);
      formData.append("returned_to_phone", returnForm.returned_to_phone || '');
      formData.append("return_notes", returnForm.return_notes || '');

      const response = await axios.post(`${baseUrl()}Lostandfounds/returnItem`, formData, config);
      
      if (response.data.status) {
        showSnackbar('Item marked as returned successfully', 'success');
        setOpenReturnDialog(false);
        setReturnForm({ returned_to_name: '', returned_to_phone: '', return_notes: '' });
        setSelectedItem(null);
        // Refresh lost and found list
        await fetchLostAndFoundItems();
        
        // Refresh view dialog if open
        if (selectedHeader) {
          const updatedHeader = await fetchSingleLostAndFound(selectedHeader.id);
          if (updatedHeader) {
            const room = rooms.find(r => r.id === updatedHeader.room_id || r.product_id === updatedHeader.room_id.toString());
            setSelectedHeader({
              ...updatedHeader,
              room_name: updatedHeader.room_name || `Room ${updatedHeader.room_id}`,
              room_number: room ? extractRoomNumber(room.name) : updatedHeader.room_id.toString()
            });
          }
        }
      } else {
        showSnackbar(response.data.message || 'Failed to mark item as returned', 'error');
      }
    } catch (error) {
      console.error('Error returning item:', error);
      showSnackbar('Error processing return', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchSingleLostAndFound = async (id: number): Promise<LostAndFoundHeader | null> => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Lostandfounds/getById`, formData, config);
      
      if (response.data.status) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching item details:', error);
      return null;
    }
  };

  const handleViewDetails = (header: LostAndFoundHeader) => {
    setSelectedHeader(header);
    setOpenViewDialog(true);
  };

  const handleOpenReturnDialog = (item: LostAndFoundItem) => {
    setSelectedItem(item);
    setOpenReturnDialog(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Loading state for the whole page
  const isLoading = roomsLoading || lostAndFoundLoading;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <MenuButton />
          <IonTitle>Lost & Found</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Loading indicator for initial load */}
        <IonLoading isOpen={isLoading && lostAndFoundList.length === 0} message={"Loading..."} spinner="circles" />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box sx={{ mb: 3 }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            justifyContent="space-between" 
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Lost & Found Items
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage lost and found items across rooms
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  size={isMobile ? "small" : "medium"}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenAddDialog(true)}
                disabled={roomsLoading || rooms.length === 0}
                size={isMobile ? "small" : "medium"}
                sx={{ borderRadius: 2 }}
              >
                Add Lost & Found
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white'
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Items</Typography>
                  {lostAndFoundLoading ? (
                    <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <Inventory />
                </Avatar>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white'
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Pending</Typography>
                  {lostAndFoundLoading ? (
                    <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <Typography variant="h4" fontWeight={700}>{stats.pending}</Typography>
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <Cancel />
                </Avatar>
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white'
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Returned</Typography>
                  {lostAndFoundLoading ? (
                    <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    <Typography variant="h4" fontWeight={700}>{stats.returned}</Typography>
                  )}
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <CheckCircle />
                </Avatar>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter Bar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 100 }}>
              <FilterIcon sx={{ color: '#3b82f6' }} />
              <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
            </Stack>
            
            <TextField
              size="small"
              placeholder="Search by room, item, or person..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />

            <Stack direction="row" spacing={1} sx={{ minWidth: 200 }}>
              {STATUS_OPTIONS.map((status) => (
                <Chip
                  key={status.value}
                  icon={React.cloneElement(status.icon, { 
                    sx: { color: selectedStatus === status.value ? 'white' : status.color }
                  })}
                  label={
                    <Badge
                      badgeContent={stats[status.value as keyof typeof stats] || 0}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: selectedStatus === status.value ? 'white' : status.color,
                          color: selectedStatus === status.value ? status.color : 'white',
                          fontSize: '0.7rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    >
                      {status.label}
                    </Badge>
                  }
                  onClick={() => handleStatusFilter(status.value)}
                  sx={{
                    px: 1,
                    bgcolor: selectedStatus === status.value ? status.color : 'white',
                    color: selectedStatus === status.value ? 'white' : status.color,
                    border: selectedStatus === status.value ? 'none' : `1px solid ${status.color}`,
                    '&:hover': {
                      bgcolor: selectedStatus === status.value ? status.color : alpha(status.color, 0.1),
                    }
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Paper>

        {/* List of Lost & Found Records */}
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Room</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Captured Bys</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lostAndFoundLoading ? (
                // Loading skeleton rows
                Array.from(new Array(3)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : (
                filteredList.map((record) => {
                  const totalItems = record.items.length;
                  const returnedItems = record.items.filter(i => i.return_status === 'RETURNED').length;
                  const status = returnedItems === totalItems ? 'Fully Returned' : 
                                returnedItems > 0 ? 'Partially Returned' : 'Pending';

                  return (
                    <TableRow key={record.id} hover>
                      <TableCell>{formatDate(record.captured_at)}</TableCell>
                      <TableCell>
                        <Chip 
                           label={record.room_name || `Room ${record.room_id}`}

                                                    // label={`xRoom ${record.room_name}`}

                          size="small"
                          icon={<HotelIcon />}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: alpha('#3b82f6', 0.1) }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                          </Avatar>
                          <Typography variant="body2">{record.captured_by_name || record.captured_by}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${totalItems} item${totalItems > 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={status}
                          size="small"
                          color={returnedItems === totalItems ? 'success' : returnedItems > 0 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(record)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {!lostAndFoundLoading && filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Inventory sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                    <Typography color="text.secondary">
                      {lostAndFoundError ? lostAndFoundError : (
                        searchTerm || selectedStatus !== 'all' 
                          ? 'No matching records found' 
                          : 'No lost and found records yet'
                      )}
                    </Typography>
                    {(searchTerm || selectedStatus !== 'all') && (
                      <Button 
                        variant="text" 
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedStatus('all');
                        }}
                        sx={{ mt: 2 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Loading Overlay for background operations */}
        <Backdrop 
          sx={{ 
            color: '#3b82f6', 
            zIndex: theme.zIndex.drawer + 1,
            backdropFilter: 'blur(3px)',
            bgcolor: 'rgba(255, 255, 255, 0.8)'
          }} 
          open={submitting}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="body2" sx={{ color: '#1e293b' }}>
              Processing...
            </Typography>
          </Stack>
        </Backdrop>

        {/* Add Lost & Found Dialog */}
        <Dialog 
          open={openAddDialog} 
          onClose={() => {
            setOpenAddDialog(false);
            setFormData({
              room_id: '',
              notes: '',
              items: []
            });
            setSelectedRoom(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">Add Lost & Found Items</Typography>
            <IconButton onClick={() => {
              setOpenAddDialog(false);
              setFormData({
                room_id: '',
                notes: '',
                items: []
              });
              setSelectedRoom(null);
            }} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Room Selection */}
              {roomsLoading ? (
                <Box sx={{ py: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading rooms...
                  </Typography>
                </Box>
              ) : roomsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {roomsError}
                  <Button size="small" onClick={fetchRooms} sx={{ ml: 2 }}>
                    Retry
                  </Button>
                </Alert>
              ) : (
                <Autocomplete
                  options={rooms}
                  getOptionLabel={(option) => `${option.name} (${extractRoomNumber(option.name)})`}
                  value={selectedRoom}
                  onChange={(event, newValue) => {
                    setSelectedRoom(newValue);
                    setFormData(prev => ({
                      ...prev,
                      room_id: newValue?.product_id.toString() || newValue?.product_id || ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Room"
                      required
                      size="small"
                      placeholder="Search for a room..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <HotelIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {extractRoomNumber(option.name)}
                          </Typography>
                        </Box>
                      </Stack>
                    </li>
                  )}
                  loading={roomsLoading}
                  sx={{ mb: 2 }}
                />
              )}

              {/* Notes */}
              <TextField
                fullWidth
                label="Additional Notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                size="small"
                multiline
                rows={2}
                placeholder="Any additional information about this collection..."
              />

              <Divider>
                <Chip label="Add Items" />
              </Divider>

              {/* Add Item Form */}
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    New Item
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Item Name"
                        value={currentItem.item_name}
                        onChange={(e) => setCurrentItem({...currentItem, item_name: e.target.value})}
                        size="small"
                        placeholder="e.g., iPhone, Wallet, Keys"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Item Notes"
                        value={currentItem.notes}
                        onChange={(e) => setCurrentItem({...currentItem, notes: e.target.value})}
                        size="small"
                        placeholder="Color, description, etc."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<PhotoCamera />}
                          size="small"
                        >
                          Upload Image
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageUpload}
                          />
                        </Button>
                        {currentItem.item_image && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Click to enlarge">
                              <IconButton 
                                onClick={() => handleImageClick(currentItem.item_image || '', currentItem.item_name)}
                                size="small"
                                sx={{ p: 0 }}
                              >
                                <Avatar 
                                  src={currentItem.item_image} 
                                  variant="rounded"
                                  sx={{ 
                                    width: 40, 
                                    height: 40,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.1)'
                                    }
                                  }} 
                                />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => setCurrentItem({...currentItem, item_image: ''})}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddItem}
                        size="small"
                        disabled={!currentItem.item_name.trim()}
                      >
                        Add Item to List
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Items List */}
              {formData.items.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Items to be Added ({formData.items.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Image</TableCell>
                          <TableCell>Item Name</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {item.item_image ? (
                                <Tooltip title="Click to enlarge">
                                  <IconButton 
                                    onClick={() => handleImageClick(item.item_image || '', item.item_name)}
                                    size="small"
                                    sx={{ p: 0 }}
                                  >
                                    <Avatar 
                                      src={item.item_image} 
                                      sx={{ 
                                        width: 30, 
                                        height: 30,
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                          transform: 'scale(1.1)'
                                        }
                                      }} 
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Image sx={{ fontSize: 30, color: 'grey.300' }} />
                              )}
                            </TableCell>
                            <TableCell>{item.item_name}</TableCell>
                            <TableCell>{item.notes || '-'}</TableCell>
                            <TableCell align="right">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => {
              setOpenAddDialog(false);
              setFormData({
                room_id: '',
                notes: '',
                items: []
              });
              setSelectedRoom(null);
            }} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={formData.items.length === 0 || !formData.room_id || submitting}
            >
              {submitting ? 'Saving...' : 'Save Lost & Found'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            bgcolor: 'info.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">Lost & Found Details</Typography>
            <IconButton onClick={() => setOpenViewDialog(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {selectedHeader && (
              <Stack spacing={3}>
                {/* Header Info */}
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2">{formatDate(selectedHeader.captured_at)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Room</Typography>
                        <Typography variant="body2">{selectedHeader.room_name || `Room ${selectedHeader.room_id}`}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Captured By</Typography>
                        <Typography variant="body2">{selectedHeader.captured_by_name || selectedHeader.captured_by}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Total Items</Typography>
                        <Typography variant="body2">{selectedHeader.items.length}</Typography>
                      </Grid>
                      {selectedHeader.notes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Notes</Typography>
                          <Typography variant="body2">{selectedHeader.notes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Items Table */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Image</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Returned To</TableCell>
                        <TableCell>Returned By</TableCell>
                        <TableCell>Returned At</TableCell>
                        {/* <TableCell align="right">Actions</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedHeader.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.item_image ? (
                              <Tooltip title="Click to enlarge">
                                <IconButton 
                                  onClick={() => handleImageClick(item.item_image || '', item.item_name)}
                                  size="small"
                                  sx={{ p: 0 }}
                                >
                                  <Avatar 
                                    src={item.item_image} 
                                    sx={{ 
                                      width: 40, 
                                      height: 40,
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.1)'
                                      }
                                    }} 
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Image sx={{ color: 'grey.300' }} />
                            )}
                          </TableCell>
                          <TableCell>{item.notes || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.return_status || 'NOT_RETURNED'}
                              size="small"
                              color={item.return_status === 'RETURNED' ? 'success' : 'default'}
                              icon={item.return_status === 'RETURNED' ? <CheckCircle /> : <Cancel />}
                            />
                          </TableCell>
                          <TableCell>
                            {[item.returned_to_name, item.returned_to_phone, item.return_notes]
                              .filter(Boolean)
                              .join(' - ') || '-'}
                          </TableCell>
                          <TableCell>{item.returned_by_name || '-'}</TableCell>
                          <TableCell>
                            {item.returned_at ? formatShortDate(item.returned_at) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {/* {item.return_status !== 'RETURNED' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircle />}
                                onClick={() => {
                                  setOpenViewDialog(false);
                                  handleOpenReturnDialog(item);
                                }}
                                sx={{ borderRadius: 2 }}
                              >
                                Return
                              </Button>
                            )} */}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Return Item Dialog */}
        <Dialog
          open={openReturnDialog}
          onClose={() => {
            setOpenReturnDialog(false);
            setSelectedItem(null);
            setReturnForm({ returned_to_name: '', returned_to_phone: '', return_notes: '' });
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle />
              <Typography variant="h6">Return Item</Typography>
            </Stack>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {selectedItem && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50', p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {selectedItem.item_image && (
                      <Tooltip title="Click to enlarge">
                        <IconButton 
                          onClick={() => handleImageClick(selectedItem.item_image || '', selectedItem.item_name)}
                          sx={{ p: 0 }}
                        >
                          <Avatar 
                            src={selectedItem.item_image} 
                            variant="rounded"
                            sx={{ 
                              width: 50, 
                              height: 50,
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.1)'
                              }
                            }} 
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Item: <strong>{selectedItem.item_name}</strong>
                      </Typography>
                      {selectedItem.notes && (
                        <Typography variant="body2" color="text.secondary">
                          Notes: {selectedItem.notes}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Card>
              )}

              <TextField
                fullWidth
                label="Returned To (Name)"
                value={returnForm.returned_to_name}
                onChange={(e) => setReturnForm({...returnForm, returned_to_name: e.target.value})}
                required
                placeholder="Guest or staff name"
              />

              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={returnForm.returned_to_phone}
                onChange={(e) => setReturnForm({...returnForm, returned_to_phone: e.target.value})}
                placeholder="Contact number"
              />

              <TextField
                fullWidth
                label="Return Notes"
                value={returnForm.return_notes}
                onChange={(e) => setReturnForm({...returnForm, return_notes: e.target.value})}
                multiline
                rows={3}
                placeholder="Any additional notes about the return..."
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                setOpenReturnDialog(false);
                setSelectedItem(null);
                setReturnForm({ returned_to_name: '', returned_to_phone: '', return_notes: '' });
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReturnItem}
              variant="contained"
              color="success"
              disabled={!returnForm.returned_to_name.trim() || submitting}
            >
              {submitting ? 'Processing...' : 'Confirm Return'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Preview Dialog */}
        <Dialog
          open={openImagePreview}
          onClose={() => setOpenImagePreview(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'transparent' }}>
            <IconButton
              onClick={() => setOpenImagePreview(false)}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)',
                },
                zIndex: 1
              }}
            >
              <Close />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                bgcolor: 'rgba(0,0,0,0.9)',
                p: 2
              }}
            >
              {previewImage && (
                <>
                  <img
                    src={previewImage}
                    alt={previewItemName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ mt: 2, color: 'white' }}>
                    {previewItemName}
                  </Typography>
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default RegisterLostAndFound;